<section class="operator-onboarding">
  <header class="page-header">
    <div>
      <p class="eyebrow">Platform employee</p>
      <h2>Operator onboarding</h2>
      <p>Complete public operator registrations, link RouterOS devices, and validate fleet monitor visibility.</p>
    </div>
    <button class="btn secondary" type="button" (click)="loadOperators()" [disabled]="isLoadingOperators">
      <i class="material-icons">refresh</i>
      <span>{{ isLoadingOperators ? 'Refreshing...' : 'Refresh' }}</span>
    </button>
  </header>

  <div class="notice error" *ngIf="errorMessage">
    <i class="material-icons">error</i>
    <span>{{ errorMessage }}</span>
  </div>

  <div class="notice success" *ngIf="successMessage">
    <i class="material-icons">check_circle</i>
    <span>{{ successMessage }}</span>
  </div>

  <div class="workspace">
    <aside class="operator-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">Operator selector</p>
          <h3>{{ operators.length }} records</h3>
        </div>
      </div>

      <div class="search-box">
        <i class="material-icons">search</i>
        <input type="search" placeholder="Search active and pending operators" [(ngModel)]="searchTerm" (keyup.enter)="loadOperators()">
        <button class="icon-btn" type="button" title="Search" (click)="loadOperators()">
          <i class="material-icons">arrow_forward</i>
        </button>
      </div>

      <label class="toggle-row">
        <input type="checkbox" [(ngModel)]="showPendingOnly" (ngModelChange)="loadOperators()" [disabled]="!!searchTerm.trim()">
        <span>Default to pending operators</span>
      </label>

      <div class="operator-list">
        <button
          class="operator-row"
          type="button"
          *ngFor="let operator of operators"
          [class.active]="getOperatorUserId(operator) === selectedOperatorUserId"
          (click)="selectOperator(operator)">
          <span class="operator-copy">
            <strong>{{ getOperatorName(operator) }}</strong>
            <small>{{ operator.email || 'No email captured' }}</small>
          </span>
          <em class="status-badge">{{ getStatusLabel(operator) }}</em>
        </button>
      </div>

      <div class="empty-state" *ngIf="!isLoadingOperators && operators.length === 0">
        <i class="material-icons">person_search</i>
        <strong>No operators found</strong>
        <span>Try a wider search or clear the pending filter.</span>
      </div>
    </aside>

    <main class="flow-panel" *ngIf="selectedOperator; else selectPrompt">
      <div class="selected-header">
        <div>
          <p class="eyebrow">Selected operator</p>
          <h3>{{ getOperatorName() }}</h3>
        </div>
        <span class="ready-pill" [class.ready]="summary?.isReadyForFleetMonitor">
          <i class="material-icons">{{ summary?.isReadyForFleetMonitor ? 'verified' : 'pending_actions' }}</i>
          {{ summary?.isReadyForFleetMonitor ? 'Ready flag true' : 'Not ready yet' }}
        </span>
      </div>

      <nav class="stepper" *ngIf="summary">
        <button
          type="button"
          *ngFor="let step of steps; let index = index"
          [class.active]="index === activeStepIndex"
          [class.done]="index < activeStepIndex"
          (click)="goToStep(index)">
          <i class="material-icons">{{ step.icon }}</i>
          <span>{{ step.label }}</span>
        </button>
      </nav>

      <div class="loading-card" *ngIf="isLoadingSummary">
        <i class="material-icons">hourglass_top</i>
        Loading onboarding summary...
      </div>

      <ng-container *ngIf="summary && !isLoadingSummary">
        <section class="step-card" *ngIf="activeStepIndex === 0">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Source of truth</p>
              <h3>Outstanding steps</h3>
            </div>
          </div>

          <div class="outstanding-list">
            <span *ngFor="let step of getOutstandingSteps()">
              <i class="material-icons">radio_button_checked</i>
              {{ step }}
            </span>
          </div>

          <div class="form-grid">
            <label>
              Organisation name
              <input type="text" [(ngModel)]="organisationDraft.orgName" placeholder="Mulaudzi Transport Service">
            </label>
            <label>
              Registration number
              <input type="text" [(ngModel)]="organisationDraft.orgRegistrationNumber" placeholder="2026/001234/07">
            </label>
            <label>
              Powerlynx Location ID
              <input type="number" min="1" step="1" [(ngModel)]="organisationDraft.powerlynxLocationID" placeholder="9">
            </label>
            <label>
              Operator code (legacy fallback)
              <input type="text" [(ngModel)]="organisationDraft.operatorCode" placeholder="MTS">
            </label>
            <label>
              Operator type
              <select [(ngModel)]="organisationDraft.operatorType">
                <option value="">Not set</option>
                <option *ngFor="let type of operatorTypes" [value]="type">{{ type }}</option>
                <option *ngIf="organisationDraft.operatorType && !operatorTypes.includes(organisationDraft.operatorType)"
                        [value]="organisationDraft.operatorType">{{ organisationDraft.operatorType }}</option>
              </select>
              <small>Operator users may need to sign in again after this changes.</small>
            </label>
            <label>
              WhatsApp number
              <input type="tel" [(ngModel)]="organisationDraft.whatsAppNumber" placeholder="+27111222333">
            </label>
          </div>
        </section>

        <section class="step-card" *ngIf="activeStepIndex === 1">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Vehicles</p>
              <h3>Use existing vehicles or add new ones</h3>
            </div>
            <button class="btn secondary" type="button" (click)="addVehicle()">
              <i class="material-icons">add</i>
              <span>Add vehicle</span>
            </button>
          </div>

          <div class="existing-grid" *ngIf="hasExistingVehicles()">
            <article *ngFor="let vehicle of summary.vehicles">
              <strong>{{ formatVehicle(vehicle) }}</strong>
              <small>ID {{ vehicle.vehicleID }} · {{ vehicle.statusName || ('Status ' + (vehicle.statusID || '-')) }}</small>
            </article>
          </div>

          <div class="draft-list">
            <article class="draft-card" *ngFor="let vehicle of vehicles; let index = index">
              <div class="draft-title">
                <strong>New vehicle {{ index + 1 }}</strong>
                <button class="icon-btn danger" type="button" title="Remove vehicle" (click)="removeVehicle(index)">
                  <i class="material-icons">delete</i>
                </button>
              </div>
              <div class="form-grid compact">
                <label>Reference<input type="text" [(ngModel)]="vehicle.clientReference"></label>
                <label>Registration<input type="text" [(ngModel)]="vehicle.vehicleRegistration"></label>
                <label>VIN / fleet ID<input type="text" [(ngModel)]="vehicle.vehicleIdentificationNumber"></label>
                <label>Make<input type="text" [(ngModel)]="vehicle.vehicleManufacturerMake"></label>
                <label>Model<input type="text" [(ngModel)]="vehicle.vehicleManufacturerModel"></label>
                <label>Year<input type="number" [(ngModel)]="vehicle.vehicleYear"></label>
                <label>Seats<input type="number" [(ngModel)]="vehicle.vehicleSeatingCapacity"></label>
                <label>Label<input type="text" [(ngModel)]="vehicle.vehicleLabel"></label>
              </div>
            </article>
          </div>
        </section>

        <section class="step-card" *ngIf="activeStepIndex === 2">
          <div class="section-heading">
            <div>
              <p class="eyebrow">SIM cards</p>
              <h3>Select available SIMs or create new SIMs</h3>
            </div>
            <button class="btn secondary" type="button" (click)="addSim()">
              <i class="material-icons">add</i>
              <span>Add SIM</span>
            </button>
          </div>

          <div class="existing-grid" *ngIf="hasExistingAvailableSims()">
            <article *ngFor="let sim of summary.availableDeviceSIMs">
              <strong>{{ formatSim(sim) }}</strong>
              <small>ID {{ sim.deviceSIMID }} · {{ sim.statusName || ('Status ' + (sim.statusID || '-')) }}</small>
            </article>
          </div>

          <div class="draft-list">
            <article class="draft-card" *ngFor="let sim of sims; let index = index">
              <div class="draft-title">
                <strong>New SIM {{ index + 1 }}</strong>
                <button class="icon-btn danger" type="button" title="Remove SIM" (click)="removeSim(index)">
                  <i class="material-icons">delete</i>
                </button>
              </div>
              <div class="form-grid compact">
                <label>Reference<input type="text" [(ngModel)]="sim.clientReference"></label>
                <label>SIM number<input type="text" [(ngModel)]="sim.deviceSIMNumber"></label>
                <label>Provider<input type="text" [(ngModel)]="sim.deviceSIMProvider"></label>
                <label>Contract type<input type="text" [(ngModel)]="sim.deviceSIMContractType"></label>
              </div>
            </article>
          </div>
        </section>

        <section class="step-card" *ngIf="activeStepIndex === 3">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Device assignment</p>
              <h3>Compare vehicle, SIM, serial, IMEI, and script generation</h3>
            </div>
            <button class="btn secondary" type="button" (click)="addAssignment()">
              <i class="material-icons">add_link</i>
              <span>Add assignment</span>
            </button>
          </div>

          <div class="assignment-list">
            <article class="assignment-card" *ngFor="let assignment of assignments; let index = index">
              <div class="draft-title">
                <strong>Assignment {{ index + 1 }}</strong>
                <button class="icon-btn danger" type="button" title="Remove assignment" (click)="removeAssignment(index)">
                  <i class="material-icons">delete</i>
                </button>
              </div>

              <div class="assignment-grid">
                <label>
                  Vehicle source
                  <select [(ngModel)]="assignment.vehicleMode">
                    <option value="existing" [disabled]="!hasExistingVehicles()">Existing vehicle</option>
                    <option value="new" [disabled]="vehicles.length === 0">New vehicle</option>
                  </select>
                </label>
                <label *ngIf="assignment.vehicleMode === 'existing'">
                  Existing vehicle
                  <select [(ngModel)]="assignment.vehicleID">
                    <option [ngValue]="null">Select vehicle</option>
                    <option *ngFor="let vehicle of summary.vehicles" [ngValue]="vehicle.vehicleID">{{ formatVehicle(vehicle) }}</option>
                  </select>
                </label>
                <label *ngIf="assignment.vehicleMode === 'new'">
                  New vehicle
                  <select [(ngModel)]="assignment.vehicleClientReference">
                    <option value="">Select vehicle</option>
                    <option *ngFor="let vehicle of vehicles" [value]="vehicle.clientReference">{{ vehicle.vehicleRegistration || vehicle.clientReference }}</option>
                  </select>
                </label>
                <label>
                  Primary SIM source
                  <select [(ngModel)]="assignment.primarySIMMode" (ngModelChange)="onPrimarySIMModeChange(assignment)">
                    <option value="none">Assign SIM later</option>
                    <option value="existing" [disabled]="!hasExistingAvailableSims()">Existing SIM</option>
                    <option value="new" [disabled]="sims.length === 0">New SIM</option>
                  </select>
                </label>
                <label *ngIf="assignment.primarySIMMode === 'existing'">
                  Primary SIM
                  <select [(ngModel)]="assignment.primarySIMID">
                    <option [ngValue]="null">Assign SIM later</option>
                    <option *ngFor="let sim of summary.availableDeviceSIMs" [ngValue]="sim.deviceSIMID"
                      [disabled]="isExistingSimAlreadySelected(sim.deviceSIMID, index, 'primary')">{{ formatSim(sim) }}</option>
                  </select>
                </label>
                <label *ngIf="assignment.primarySIMMode === 'new'">
                  New primary SIM
                  <select [(ngModel)]="assignment.primarySIMClientReference">
                    <option value="">Assign SIM later</option>
                    <option *ngFor="let sim of sims" [value]="sim.clientReference">{{ sim.deviceSIMNumber || sim.clientReference }}</option>
                  </select>
                </label>
                <label>
                  Secondary SIM source
                  <select [(ngModel)]="assignment.secondarySIMMode" [disabled]="!hasPrimarySIM(assignment)"
                    (ngModelChange)="onSecondarySIMModeChange(assignment)">
                    <option value="none">Assign SIM later</option>
                    <option value="existing" [disabled]="!hasExistingAvailableSims()">Existing SIM</option>
                    <option value="new" [disabled]="sims.length === 0">New SIM</option>
                  </select>
                </label>
                <label *ngIf="assignment.secondarySIMMode === 'existing'">
                  Secondary SIM / failover
                  <select [(ngModel)]="assignment.secondarySIMID">
                    <option [ngValue]="null">Assign SIM later</option>
                    <option *ngFor="let sim of summary.availableDeviceSIMs" [ngValue]="sim.deviceSIMID"
                      [disabled]="isExistingSimAlreadySelected(sim.deviceSIMID, index, 'secondary')">{{ formatSim(sim) }}</option>
                  </select>
                </label>
                <label *ngIf="assignment.secondarySIMMode === 'new'">
                  New secondary SIM / failover
                  <select [(ngModel)]="assignment.secondarySIMClientReference">
                    <option value="">Assign SIM later</option>
                    <option *ngFor="let sim of sims" [value]="sim.clientReference">{{ sim.deviceSIMNumber || sim.clientReference }}</option>
                  </select>
                </label>
                <label>Model<input type="text" [(ngModel)]="assignment.deviceModel"></label>
                <label>Device type<input type="text" [(ngModel)]="assignment.deviceType"></label>
                <label>Serial number<input type="text" [(ngModel)]="assignment.deviceSerialNumber"></label>
                <label>IMEI<input type="text" [(ngModel)]="assignment.deviceImei"></label>
              </div>

              <label class="toggle-row inline">
                <input type="checkbox" [(ngModel)]="assignment.generateRouterOsScript">
                <span>Generate RouterOS script after linking</span>
              </label>

              <div class="comparison-row">
                <span><b>Vehicle</b>{{ getVehicleLabelByAssignment(assignment) }}</span>
                <span><b>Primary SIM</b>{{ getSimLabelByAssignment(assignment, 'primary') }}</span>
                <span><b>Secondary SIM</b>{{ getSimLabelByAssignment(assignment, 'secondary') }}</span>
                <span><b>Serial</b>{{ assignment.deviceSerialNumber || '-' }}</span>
                <span><b>IMEI</b>{{ assignment.deviceImei || '-' }}</span>
                <span><b>Script</b>{{ assignment.generateRouterOsScript ? 'Generate' : 'Skip' }}</span>
              </div>
            </article>
          </div>
        </section>

        <section class="step-card" *ngIf="activeStepIndex === 4">
          <div class="section-heading">
            <div>
              <p class="eyebrow">RouterOS scripts</p>
              <h3>Manual install package matching</h3>
            </div>
          </div>

          <div class="existing-grid" *ngIf="summary.devices?.length">
            <article *ngFor="let device of summary.devices">
              <strong>{{ formatDevice(device) }}</strong>
              <small>Device ID {{ getDeviceId(device) || '-' }} · Vehicle ID {{ getDeviceVehicleId(device) || '-' }}</small>
              <div class="script-actions">
                <button class="btn secondary slim" type="button" (click)="viewScript(getDeviceId(device))">
                  <i class="material-icons">visibility</i>
                  <span>View script</span>
                </button>
                <button class="btn secondary slim" type="button" (click)="regenerateScript(getDeviceId(device))">
                  <i class="material-icons">refresh</i>
                  <span>Regenerate script</span>
                </button>
              </div>
            </article>
          </div>

          <div class="script-list" *ngIf="getScriptRows().length; else noScripts">
            <article class="script-card" *ngFor="let script of getScriptRows()">
              <div class="script-card-header">
                <div class="script-meta">
                  <span><b>Device ID</b>{{ script.deviceID || '-' }}</span>
                  <span><b>Vehicle ID</b>{{ script.vehicleID || '-' }}</span>
                  <span><b>Primary SIM ID</b>{{ script.primarySIMID || script.deviceSIMID || '-' }}</span>
                  <span><b>Secondary SIM ID</b>{{ script.secondarySIMID || '-' }}</span>
                  <span><b>Vehicle</b>{{ script.vehicleRegistration || '-' }}</span>
                  <span><b>Serial</b>{{ script.deviceSerialNumber || '-' }}</span>
                </div>
                <button
                  class="btn secondary slim copy-script-button"
                  type="button"
                  [disabled]="!script.routerOsScript"
                  (click)="copyScript(script)">
                  <i class="material-icons">{{ isScriptCopied(script) ? 'check' : 'content_copy' }}</i>
                  <span>{{ isScriptCopied(script) ? 'Copied' : 'Copy full script' }}</span>
                </button>
              </div>
              <div class="script-landing-url" *ngIf="script.publicDeparturesUrl">
                <strong>Passenger landing page</strong>
                <a [href]="script.publicDeparturesUrl" target="_blank" rel="noopener">{{ script.publicDeparturesUrl }}</a>
              </div>
              <pre>{{ script.routerOsScript || 'No script returned.' }}</pre>
            </article>
          </div>

          <ng-template #noScripts>
            <div class="empty-state">
              <i class="material-icons">terminal</i>
              <strong>No scripts displayed yet</strong>
              <span>Complete onboarding or regenerate a script for an already-linked device.</span>
            </div>
          </ng-template>
        </section>

        <section class="step-card" *ngIf="activeStepIndex === 5">
          <div class="section-heading">
            <div>
              <p class="eyebrow">Fleet monitor validation</p>
              <h3>Confirm employee and operator visibility</h3>
            </div>
            <div class="button-row">
              <button class="btn secondary" type="button" (click)="validateEmployeeFleetMonitor()" [disabled]="isValidatingFleet">
                <i class="material-icons">domain</i>
                <span>Employee validation</span>
              </button>
              <button class="btn secondary" type="button" (click)="validateOperatorScopedFleetMonitor()" [disabled]="isValidatingFleet">
                <i class="material-icons">person_pin_circle</i>
                <span>Operator-scoped validation</span>
              </button>
            </div>
          </div>

          <div class="table-wrap" *ngIf="fleetRows.length">
            <h4>Employee organisation rows</h4>
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Registration</th>
                  <th>Status</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of fleetRows">
                  <td>{{ row.vehicleID || row.vehicleId || '-' }}</td>
                  <td>{{ row.vehicleRegistration || row.registration || '-' }}</td>
                  <td>{{ getHeartbeatStatus(row) }}</td>
                  <td>{{ row.lastSeenAt || row.LastSeenAt || row.lastSeen || 'NeverSeen' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="table-wrap" *ngIf="operatorScopedFleetRows.length">
            <h4>Operator-scoped rows</h4>
            <table>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Registration</th>
                  <th>Status</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of operatorScopedFleetRows">
                  <td>{{ row.vehicleID || row.vehicleId || '-' }}</td>
                  <td>{{ row.vehicleRegistration || row.registration || '-' }}</td>
                  <td>{{ getHeartbeatStatus(row) }}</td>
                  <td>{{ row.lastSeenAt || row.LastSeenAt || row.lastSeen || 'NeverSeen' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <footer class="flow-actions">
          <div class="step-actions">
            <button class="btn secondary" type="button" (click)="previousStep()" [disabled]="activeStepIndex === 0">Back</button>
            <button class="btn secondary" type="button" (click)="nextStep()" [disabled]="activeStepIndex === steps.length - 1">Next</button>
          </div>
          <button class="btn primary" type="button" (click)="submitCompletion()" [disabled]="isCompleting">
            <i class="material-icons">rocket_launch</i>
            <span>{{ isCompleting ? 'Completing...' : 'Complete onboarding' }}</span>
          </button>
        </footer>
      </ng-container>
    </main>

    <ng-template #selectPrompt>
      <main class="flow-panel prompt">
        <i class="material-icons">badge</i>
        <h3>Select an operator</h3>
        <p>Choose a pending or active operator to load their onboarding summary and continue setup.</p>
      </main>
    </ng-template>
  </div>
</section>
